import type { LucideIcon } from "lucide-react"

import type { LookupType } from "@ibada/shared-types/lookups"
import type { PeriodPreset } from "@/shared/lib/dates/period-range"

/**
 * The Finance Report Catalog registry. Each predefined report is one declarative `FinanceReportEntry`:
 * curation (category/icon/keywords), the minimal config it needs, which of the three backends supplies its
 * data, how the viewer renders it, and how it exports. Adding a report = adding one entry. The engine stays
 * generic (the ADR-0013 (Reporting Platform Foundation) reporting platform + the accounting endpoints); the registry speaks church language.
 */

/**
 * Which part of the church a report is ABOUT — the catalog's tab row.
 *
 * Separate from `FinanceReportCategory`, which groups reports into sections INSIDE a tab. A module is the
 * question "whose reports are these"; a category is "what kind". Keeping them apart is what lets a new
 * module arrive as a registry entry and a label rather than a redesign.
 *
 * `All` and `Favorites` are deliberately absent: they are views computed over every module, not modules.
 */
export enum ReportModule {
  Finance = "finance",
  Members = "members",
  Activities = "activities",
}

export enum FinanceReportCategory {
  FinancialStatements = "financial-statements",
  Transactions = "transactions",
  Funds = "funds",
  Budgets = "budgets",
  Activities = "activities",
  Giving = "giving",
  Assets = "assets",
  Audit = "audit",
  // Sections for the modules beyond finance. Named for what the reader is looking at, not the entity.
  Membership = "membership",
  Directory = "directory",
  Participation = "participation",
}

/** Ordered category metadata for the catalog layout. */
export interface FinanceReportCategoryMeta {
  category: FinanceReportCategory
  labelKey: string
}

/** The fixed set of config field kinds — a renderer switch, NOT a generic form builder. */
export enum ReportFieldKind {
  Period = "period", // FinancePeriodPicker -> { from, to, preset }
  Month = "month", // "YYYY-MM"
  Date = "date", // "YYYY-MM-DD"
  Account = "account", // Select fed by useAccountingChartQuery
  Branch = "branch", // ReportRecordSelect(Branches) — parish context only
  Fund = "fund",
  Activity = "activity",
  Lookup = "lookup", // generic ReportRecordSelect (group / ministry / contributor / …)
}

export interface ReportFieldSpec {
  /** Param key carried in the URL + sent to the backend, e.g. "accountingAccountId". */
  key: string
  kind: ReportFieldKind
  labelKey: string
  required?: boolean
  /** For kind === Lookup. */
  lookupType?: LookupType
  /** For kind === Period. */
  defaultPreset?: PeriodPreset
}

export interface ReportConfigSchema {
  fields: ReportFieldSpec[]
}

/** Which of the three backends supplies a report's data. */
export type ReportDataSource =
  | { kind: "accounting"; endpoint: AccountingEndpoint; section?: StatementSection }
  | { kind: "engine-template"; templateId: string }
  | { kind: "finance-resource"; resource: FinanceResource }

/** The three reports carved out of the single /finance/accounting/statements payload. */
export enum StatementSection {
  BalanceSheet = "balance-sheet",
  IncomeStatement = "income-statement",
  CashFlow = "cash-flow",
}

export enum AccountingEndpoint {
  Statements = "statements",
  TrialBalance = "trial-balance",
  Ledger = "ledger",
  Journal = "journal",
  Chart = "chart",
}

/** The finance-resource endpoints the catalog actually consumes — one member per wired report, no aspirations. */
export enum FinanceResource {
  FundActivity = "fund-activity",
  FundUtilization = "fund-utilization",
  ActivityBudgetPerformance = "activity-budget-performance",
  DepartmentSummary = "department-summary",
  Reconciliation = "reconciliation",
}

/** How the viewer renders the report. `result-panel` = the normalized shell body; `custom` = an escape hatch. */
export interface ReportViewerSpec {
  layout: "result-panel" | "custom"
  defaultView?: "table" | "chart" | "cards"
  /** Key into the viewers map when layout === "custom" (reports that don't fit columns/rows). */
  customRenderer?: "statements" | "journal"
}

/** What a drill-down opens: another report, or a page elsewhere in the app (e.g. a transaction's audit). */
export enum ReportDrilldownTargetKind {
  Report = "REPORT",
  Route = "ROUTE",
}

/**
 * A cell that answers "why is this number what it is" by opening the thing that proves it.
 *
 * A figure with no drill-down is the number a treasurer cannot trust: it asserts an answer and hides the
 * evidence. Declaring the drill-down here (rather than hard-coding a link into one table) means any report can
 * gain one, and the viewer stays generic.
 *
 * `params` maps a key expected by the TARGET (a report's config key, or a `:param` in a route path) to the key
 * on THIS report's row that supplies it. A row that cannot supply every one of them is not drillable, and
 * renders as plain text rather than a dead link.
 */
export type ReportDrilldownSpec = {
  /** The column whose cells become links. */
  column: string
  params: Record<string, string>
} & (
  | { kind: ReportDrilldownTargetKind.Report; reportId: string }
  /** Branch-admin-relative path with `:param` placeholders, e.g. `/finance/investigations/investigation/transaction/:transactionId`. */
  | { kind: ReportDrilldownTargetKind.Route; path: string }
)

export enum ReportExportKind {
  /** ADR-0013 (Reporting Platform Foundation) engine export → PDF/XLSX/CSV via /reports/export (registry-expressible reports). */
  Engine = "engine",
  /**
   * Client-side CSV built from the report's data. Tabular accounting reports hold every row already; the
   * journal is paginated, so its exporter re-fetches the whole journal rather than write the visible page.
   */
  ClientCsv = "client-csv",
  /** No file export yet (statements); Print still works. */
  Unavailable = "unavailable",
}

export type ReportExportSpec =
  | { kind: ReportExportKind.Engine }
  | { kind: ReportExportKind.ClientCsv }
  | { kind: ReportExportKind.Unavailable; reason: "no-backend" }

export enum ReportBadge {
  New = "new",
  Beta = "beta",
  ComingSoon = "coming-soon",
  /**
   * Reads the PARISH's own books, on a catalog whose other reports are church-wide.
   *
   * Not decoration: these are the accounting reports, and they resolve one branch. Sitting unlabelled beside
   * consolidated templates they would read as "the whole church's trial balance", which is a different and
   * wrong statement. The badge is what makes offering them here honest.
   */
  Parish = "parish",
}

export interface FinanceReportEntry {
  /** Stable slug — the `:reportId` route segment (e.g. "trial-balance", "income-by-category"). */
  id: string
  /** Which catalog tab this report appears under. */
  module: ReportModule
  category: FinanceReportCategory
  icon: LucideIcon
  /** Optional for engine entries: falls back to the template's `labelKey` from GET /reports/templates. */
  titleKey?: string
  descriptionKey?: string
  keywords: string[]
  badge?: ReportBadge
  /**
   * The day this report stops being NEW (ISO `YYYY-MM-DD`), rather than a badge someone must remember to
   * take off.
   *
   * More templates will keep arriving, and "New" is how a church notices them. But a New badge that never
   * expires is worse than none: once several of them are permanent, the badge stops meaning "look here" and
   * becomes decoration. Declaring the end date with the template makes forgetting impossible.
   */
  newUntil?: string
  disabled?: boolean
  /** Omitted for engine entries → derived at runtime from the template's `runtimeInputs`. */
  configSchema?: ReportConfigSchema
  dataSource: ReportDataSource
  viewer: ReportViewerSpec
  /** Optional: makes one column's values clickable, opening the report that substantiates them. */
  drilldown?: ReportDrilldownSpec
  export: ReportExportSpec
}

/**
 * The badge a report is actually wearing today.
 *
 * `newUntil` wins while it lasts — a brand-new report is worth pointing at more than whatever else it might
 * be flagged as — and once the date passes the entry falls back to its own badge, or to none.
 */
export function resolveReportBadge(
  entry: Pick<FinanceReportEntry, "badge" | "newUntil">,
  now: Date = new Date(),
): ReportBadge | undefined {
  if (entry.newUntil) {
    const until = new Date(`${entry.newUntil}T23:59:59.999Z`)
    if (!Number.isNaN(until.getTime()) && until.getTime() >= now.getTime()) return ReportBadge.New
  }
  return entry.badge
}
